import {
    type Component,
    type DefineComponent,
    defineAsyncComponent,
} from 'vue';
import { type LoggerOptions, createLogger } from './../pretty';

import { Maybe } from '@precon-tools-suite-by-shared/js-utils';
import { ComponentNotDefinedError } from '../errors';

export interface PromiseLoader<T> {
    (): Promise<T>;
}

export type NamedLoaderFactory = (
    name: string,
    fallback?: string | Component | null
) => PromiseLoader<DefineComponent>;
export type NamedLoader = (
    name: string,
    fallback?: string | Component | null
) => Promise<DefineComponent>;

export interface NamedAsyncComponentOptions {
    errorHandler?: (err: Error, attempts: number) => boolean;
    maxAttempts?: number;
    delay?: number;
    timeout?: number;
    suspensible?: boolean;
    loadingComponent?: Component;
    fallback?: string | Component | null;
    errorComponent?: Component;
}

export interface NamedRepositoryOptions extends LoggerOptions {
    modules?: Record<string, PromiseLoader<unknown>>;
    pathPrefix?: string;
    pathSuffix?: string;
    asyncComponentOptions?: NamedAsyncComponentOptions;
}

export type NamedRepository = ReturnType<typeof createNamedRepository>;

export function createNamedRepository(options: NamedRepositoryOptions = {}) {
    const extraModules = new Map<string, PromiseLoader<DefineComponent>>();

    function registerPartial(
        name: string,
        loader: PromiseLoader<DefineComponent>
    ) {
        extraModules.set(name, loader);
    }

    const logger = createLogger(options);
    const modules = options.modules ?? {};
    const pathPrefix = options.pathPrefix ?? '';
    const pathSuffix = options.pathSuffix ?? '.vue';

    const nameToPath = (name: string) => pathPrefix + name + pathSuffix;
    const pathToName = (path: string) =>
        path.substring(pathPrefix.length, path.length - pathSuffix.length);
    const paths = Object.keys(modules);
    const names = paths.map(pathToName);

    const loader = ((name: string, fallback?: string | Component | null) => {
        return Maybe.from(extraModules.get(name) ?? modules[nameToPath(name)])
            .map((x) => () => {
                logger.logLazyLoading(name);
                return x().then((p: any) => p.default);
            })
            .default(async () => {
                if (fallback) {
                    logger.logLoadingFallback(name, fallback);
                    return typeof fallback === 'string'
                        ? load(fallback)
                        : fallback;
                }
                throw logger.makeNotDefinedError(name);
            });
    }) as NamedLoaderFactory;

    const load: NamedLoader = (name, fallback) => loader(name, fallback)();

    const asyncComponent = (
        name: string,
        customOptions: NamedAsyncComponentOptions = {}
    ) => {
        const opts = {
            ...(options.asyncComponentOptions ?? {}),
            ...customOptions,
        };

        const errorHandler = opts.errorHandler ?? (() => true);
        const maxAttempts = opts.maxAttempts ?? 10;

        return defineAsyncComponent({
            loader: loader(name, opts.fallback),
            suspensible: opts.suspensible,
            delay: opts.delay,
            timeout: opts.timeout,
            loadingComponent: opts.loadingComponent,
            errorComponent: opts.errorComponent,
            onError(err, retry, fail, attempts) {
                if (
                    err instanceof SyntaxError ||
                    err instanceof ComponentNotDefinedError
                ) {
                    fail();
                    return;
                }

                logger.logLoadingAttemptFailed(err, name, attempts);

                const allowRetry = errorHandler(err, attempts);
                if (allowRetry && attempts < maxAttempts) {
                    const t = Math.max(
                        250 * Math.pow(2, Math.min(1, attempts)),
                        4000
                    );
                    setTimeout(retry, t);
                    return;
                }

                logger.logLoadingFailed(err, name, attempts);
                fail();
            },
        });
    };

    return {
        pathPrefix,
        pathSuffix,
        nameToPath,
        pathToName,
        modules,
        paths,
        names,
        loader,
        load,
        asyncComponent,
        registerPartial,
    };
}
